import { useEffect, useState } from "react";
import { useAuthStore } from "@/services/authStore";

type AccountView = "main" | "feedbacks" | "signup" | "bonus" | "report";

/**
 * The account sidebar's open state. `mounted` sticks after the first open (same
 * split as the map) so the lazy auth chunk loads once, on demand, and never
 * costs the feed's first paint; `open` drives the slide-in/out.
 */
export function useAccountPanel() {
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountMounted, setAccountMounted] = useState(false);
  const [accountView, setAccountView] = useState<AccountView>("main");
  const openAccount = () => {
    setAccountMounted(true);
    setAccountOpen(true);
  };
  // Deep-link the sidebar straight into the Feedbacks view (from a check-in or
  // a passive dwell toast).
  const openFeedbacks = () => {
    setAccountView("feedbacks");
    openAccount();
  };
  // Deep-link into the stamp card (from the check-in's stamp line).
  const openBonus = () => {
    setAccountView("bonus");
    openAccount();
  };
  // Deep-link straight into sign-up (from the locked Offers tab + the guest
  // sign-up nudge).
  const openSignUp = () => {
    setAccountView("signup");
    openAccount();
  };
  // Deep-link into the report box (a Report tap; the target sits in reportStore).
  const openReport = () => {
    setAccountView("report");
    openAccount();
  };
  const closeAccount = () => {
    setAccountOpen(false);
    setAccountView("main");
  };

  // A password-recovery link landed us here with a recovery session — open
  // the sidebar straight into its set-new-password view.
  const recoveryPending = useAuthStore((s) => s.recoveryPending);
  useEffect(() => {
    if (recoveryPending) openAccount();
  }, [recoveryPending]);

  return {
    accountOpen,
    accountMounted,
    accountView,
    openAccount,
    openFeedbacks,
    openBonus,
    openSignUp,
    openReport,
    closeAccount,
  };
}
